import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Printer, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate, formatMoney } from '@/lib/format';
import type { ApiSuccess } from '@/lib/types';
import { INVOICE_STATUS_LABELS } from './labels';
import type { Invoice } from './types';

type PublicInvoice = Omit<Invoice, 'publicToken' | 'ownerId' | 'owner'> & {
  amountInWords: string;
  issuer: {
    name: string;
    address: string | null;
    email: string | null;
    phone: string | null;
    taxNumber: string | null;
  };
};

/**
 * The client-facing invoice. No session and no app shell — the token in the URL
 * is the credential. Styled to print cleanly, since this is the copy a client
 * forwards to their own accounts department.
 */
export function PublicInvoicePage() {
  const { token } = useParams<{ token: string }>();
  const base = import.meta.env.VITE_API_URL ?? '/api/v1';

  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ['public-invoice', token],
    queryFn: async () => {
      const response = await axios.get<ApiSuccess<PublicInvoice>>(
        `${base}/public/invoices/${token}`,
      );
      return response.data.data;
    },
    retry: false,
  });

  if (isLoading) return <Shell>Loading…</Shell>;

  if (error || !invoice) {
    return (
      <Shell>
        <div className="text-center">
          <XCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">This link is not valid</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The invoice may have been cancelled. Please ask your contact for a new link.
          </p>
        </div>
      </Shell>
    );
  }

  const isProforma = invoice.type === 'PROFORMA';
  const overdue = invoice.status === 'OVERDUE';
  const settled = invoice.status === 'PAID';

  return (
    <Shell>
      {invoice.status === 'VOID' && (
        <Banner tone="muted" icon={<XCircle className="h-4 w-4" />}>
          This invoice has been cancelled and is not payable.
          {invoice.voidReason && <span className="block mt-0.5">{invoice.voidReason}</span>}
        </Banner>
      )}
      {settled && (
        <Banner tone="success" icon={<CheckCircle2 className="h-4 w-4" />}>
          Paid in full{invoice.paidAt && ` on ${formatDate(invoice.paidAt)}`}. Thank you.
        </Banner>
      )}
      {overdue && (
        <Banner tone="warning" icon={<AlertTriangle className="h-4 w-4" />}>
          This invoice was due on {formatDate(invoice.dueDate)} and is still outstanding.
        </Banner>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{invoice.issuer.name}</h1>
          {invoice.issuer.address && (
            <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
              {invoice.issuer.address}
            </p>
          )}
          <p className="mt-1 text-sm text-muted-foreground">
            {[invoice.issuer.email, invoice.issuer.phone].filter(Boolean).join(' · ')}
          </p>
          {invoice.issuer.taxNumber && (
            <p className="text-sm text-muted-foreground">TRN {invoice.issuer.taxNumber}</p>
          )}
        </div>

        <div className="text-right">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {isProforma ? 'Proforma invoice' : 'Tax invoice'}
          </div>
          <div className="text-lg font-semibold">{invoice.number}</div>
          <div className="mt-1 text-sm text-muted-foreground">
            Issued {formatDate(invoice.issueDate)}
          </div>
          <div className="text-sm text-muted-foreground">Due {formatDate(invoice.dueDate)}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {INVOICE_STATUS_LABELS[invoice.status]}
          </div>
        </div>
      </div>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <div>
          <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">Billed to</div>
          <div className="font-medium">{invoice.company.name}</div>
          {invoice.contact && (
            <div className="text-sm text-muted-foreground">
              {invoice.contact.firstName} {invoice.contact.lastName ?? ''}
            </div>
          )}
          {invoice.billingTrn && (
            <div className="text-sm text-muted-foreground">TRN {invoice.billingTrn}</div>
          )}
        </div>
        {invoice.poReference && (
          <div className="sm:text-right">
            <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
              Your reference
            </div>
            <div className="text-sm">{invoice.poReference}</div>
          </div>
        )}
      </div>

      <div className="mt-8 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-2 font-medium">Description</th>
              <th className="py-2 text-right font-medium">Qty</th>
              <th className="py-2 text-right font-medium">Rate</th>
              <th className="py-2 text-right font-medium">Tax</th>
              <th className="py-2 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, index) =>
              item.isHeading ? (
                // A heading is a section title the sender typed, not a charge.
                <tr key={item.id ?? index} className="border-b">
                  <td colSpan={5} className="pb-1.5 pt-4 text-xs font-semibold uppercase tracking-wide">
                    {item.description}
                  </td>
                </tr>
              ) : (
                <tr key={item.id ?? index} className="border-b align-top">
                  <td className="whitespace-pre-line py-2.5">{item.description}</td>
                  <td className="tabular py-2.5 text-right">
                    {item.quantity}
                    {item.unit && <span className="ml-1 text-muted-foreground">{item.unit}</span>}
                  </td>
                  <td className="tabular py-2.5 text-right">
                    {formatMoney(item.unitPrice, invoice.currency)}
                  </td>
                  <td className="tabular py-2.5 text-right text-muted-foreground">
                    {item.taxRatePercent}%
                  </td>
                  <td className="tabular py-2.5 text-right font-medium">
                    {formatMoney(item.lineTotal, invoice.currency)}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex justify-end">
        <dl className="w-full max-w-xs space-y-1.5 text-sm">
          <Row label="Subtotal" value={formatMoney(invoice.subtotal, invoice.currency)} />
          {invoice.discountTotal && invoice.discountTotal !== '0.00' && (
            <Row
              label="Discount"
              value={`− ${formatMoney(invoice.discountTotal, invoice.currency)}`}
            />
          )}
          {invoice.taxBreakdown?.map((tax) => (
            <Row
              key={tax.ratePercent}
              label={`VAT ${tax.ratePercent}% on ${formatMoney(tax.taxableAmount, invoice.currency)}`}
              value={formatMoney(tax.taxAmount, invoice.currency)}
            />
          ))}
          {invoice.otherCharges && invoice.otherCharges !== '0.00' && (
            <Row label="Other charges" value={formatMoney(invoice.otherCharges, invoice.currency)} />
          )}
          {invoice.withholdingTax && invoice.withholdingTax !== '0.00' && (
            <Row
              label="Withholding tax"
              value={`− ${formatMoney(invoice.withholdingTax, invoice.currency)}`}
            />
          )}
          {invoice.roundingAdjustment && invoice.roundingAdjustment !== '0.00' && (
            <Row
              label="Rounding"
              value={formatMoney(invoice.roundingAdjustment, invoice.currency)}
            />
          )}

          <div className="flex justify-between border-t pt-2 text-base font-semibold">
            <dt>Total</dt>
            <dd className="tabular">{formatMoney(invoice.grandTotal, invoice.currency)}</dd>
          </div>

          {invoice.amountPaid && invoice.amountPaid !== '0.00' && (
            <>
              <Row label="Paid" value={`− ${formatMoney(invoice.amountPaid, invoice.currency)}`} />
              <div className="flex justify-between border-t pt-2 font-semibold">
                <dt>Balance due</dt>
                <dd className="tabular">{formatMoney(invoice.balanceDue, invoice.currency)}</dd>
              </div>
            </>
          )}
        </dl>
      </div>

      <p className="mt-4 text-right text-xs text-muted-foreground">{invoice.amountInWords}</p>

      {invoice.payments && invoice.payments.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-2 text-sm font-semibold">Payments received</h2>
          <ul className="space-y-1 text-sm">
            {invoice.payments.map((allocation) => (
              <li key={allocation.id} className="flex justify-between text-muted-foreground">
                <span>
                  {formatDate(allocation.payment.date)} · {allocation.payment.number}
                  {allocation.payment.reference && ` · ${allocation.payment.reference}`}
                </span>
                <span className="tabular">
                  {formatMoney(allocation.amount, invoice.currency)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(invoice.notes || invoice.terms) && (
        <div className="mt-8 space-y-4 border-t pt-6 text-sm">
          {invoice.notes && (
            <div>
              <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">Notes</div>
              <p className="whitespace-pre-line text-muted-foreground">{invoice.notes}</p>
            </div>
          )}
          {invoice.terms && (
            <div>
              <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
                Payment terms
              </div>
              <p className="whitespace-pre-line text-muted-foreground">{invoice.terms}</p>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 flex justify-end print:hidden">
        <Button variant="outline" onClick={() => window.print()}>
          <Printer className="h-4 w-4" />
          Print or save as PDF
        </Button>
      </div>
    </Shell>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-muted/40 px-4 py-10 print:bg-white print:p-0">
      <div className="mx-auto max-w-3xl rounded-lg border bg-background p-6 shadow-sm sm:p-10 print:border-0 print:shadow-none">
        {children}
      </div>
    </div>
  );
}

function Banner({
  tone,
  icon,
  children,
}: {
  tone: 'success' | 'warning' | 'muted';
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const styles = {
    success: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    warning: 'border-amber-300 bg-amber-50 text-amber-900',
    muted: 'border-border bg-muted text-muted-foreground',
  }[tone];

  return (
    <div className={`mb-6 flex items-start gap-2 rounded-md border px-4 py-3 text-sm ${styles}`}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span>{children}</span>
    </div>
  );
}
